/**
 * Date Formatting Utility
 * 
 * Formats dates based on user preferences
 * Falls back to DD/MM/YYYY if preferences not available
 */

import { format } from 'date-fns';
import type { UserPreferences } from '../types/preference.types';

/**
 * Format date based on user preference
 * 
 * Supported formats:
 * - MM/DD/YYYY → 12/25/2024
 * - DD/MM/YYYY → 25/12/2024
 * - YYYY-MM-DD → 2024-12-25
 * - DD-MM-YYYY → 25-12-2024
 * - MM-DD-YYYY → 12-25-2024
 * - DDD MMM DD YYYY → Fri Oct 31 2025
 */
export function formatDateWithPreferences(
  date: Date | string,
  preferences?: UserPreferences | null,
  options?: {
    fallbackFormat?: string; // Fallback if preferences not available
  }
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  const dateFormat = preferences?.dateFormat || options?.fallbackFormat || 'DD/MM/YYYY';

  // Map preference format to date-fns format pattern
  let formatPattern: string;
  
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      formatPattern = 'MM/dd/yyyy';
      break;
    case 'DD/MM/YYYY':
      formatPattern = 'dd/MM/yyyy';
      break;
    case 'YYYY-MM-DD':
      formatPattern = 'yyyy-MM-dd';
      break;
    case 'DD-MM-YYYY':
      formatPattern = 'dd-MM-yyyy';
      break;
    case 'MM-DD-YYYY':
      formatPattern = 'MM-dd-yyyy';
      break;
    case 'DDD MMM DD YYYY':
      formatPattern = 'EEE MMM dd yyyy';
      break;
    default:
      // Fallback to DD/MM/YYYY
      formatPattern = 'dd/MM/yyyy';
  }

  try {
    return format(dateObj, formatPattern);
  } catch (error) {
    console.warn('Error formatting date:', error);
    // Fallback to ISO string
    return dateObj.toISOString().split('T')[0];
  }
}

/**
 * Format date for input fields (always returns YYYY-MM-DD for HTML date inputs)
 */
export function formatDateForInput(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return format(dateObj, 'yyyy-MM-dd');
}

/**
 * Parse date string based on user preference
 * Supports multiple formats for parsing
 */
export function parseDateWithPreferences(
  dateString: string,
  preferences?: UserPreferences | null
): Date | null {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const trimmed = dateString.trim();
  if (!trimmed) {
    return null;
  }

  // Try ISO format first (YYYY-MM-DD) - this is what comes from database
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10) - 1; // Month is 0-indexed
    const day = parseInt(isoMatch[3], 10);
    const date = new Date(year, month, day);
    if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
      return date;
    }
  }

  const dateFormat = preferences?.dateFormat || 'DD/MM/YYYY';

  // Parse based on user's preferred format
  switch (dateFormat) {
    case 'MM/DD/YYYY':
      // Try MM/DD/YYYY format
      const mmddyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (mmddyyyyMatch) {
        const month = parseInt(mmddyyyyMatch[1], 10) - 1;
        const day = parseInt(mmddyyyyMatch[2], 10);
        const year = parseInt(mmddyyyyMatch[3], 10);
        const date = new Date(year, month, day);
        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          return date;
        }
      }
      break;
    
    case 'DD/MM/YYYY':
      // Try DD/MM/YYYY format
      const ddmmyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (ddmmyyyyMatch) {
        const day = parseInt(ddmmyyyyMatch[1], 10);
        const month = parseInt(ddmmyyyyMatch[2], 10) - 1;
        const year = parseInt(ddmmyyyyMatch[3], 10);
        const date = new Date(year, month, day);
        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          return date;
        }
      }
      break;
    
    case 'YYYY-MM-DD':
      // Already handled above as ISO format
      break;
    
    case 'DD-MM-YYYY':
      // Try DD-MM-YYYY format
      const ddmmyyyyDashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
      if (ddmmyyyyDashMatch) {
        const day = parseInt(ddmmyyyyDashMatch[1], 10);
        const month = parseInt(ddmmyyyyDashMatch[2], 10) - 1;
        const year = parseInt(ddmmyyyyDashMatch[3], 10);
        const date = new Date(year, month, day);
        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          return date;
        }
      }
      break;
    
    case 'MM-DD-YYYY':
      // Try MM-DD-YYYY format
      const mmddyyyyDashMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
      if (mmddyyyyDashMatch) {
        const month = parseInt(mmddyyyyDashMatch[1], 10) - 1;
        const day = parseInt(mmddyyyyDashMatch[2], 10);
        const year = parseInt(mmddyyyyDashMatch[3], 10);
        const date = new Date(year, month, day);
        if (date.getDate() === day && date.getMonth() === month && date.getFullYear() === year) {
          return date;
        }
      }
      break;
    
    case 'DDD MMM DD YYYY':
      // Try parsing day name format (e.g., "Fri Oct 31 2025")
      // This is primarily for display, but we can try to parse it
      try {
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      } catch {
        // Ignore parsing errors
      }
      break;
  }

  // Fallback: try standard Date parsing
  const fallback = new Date(trimmed);
  if (!isNaN(fallback.getTime())) {
    return fallback;
  }

  return null;
}

